/**
 * Google Place選択ダイアログ
 *
 * Yahoo APIで取得したPlaceをGoogle APIで再取得する際に、
 * 候補一覧を表示してユーザーに選択させるダイアログ。
 * 地図上で既存のPlaceと候補の位置を確認できる。
 */

import { AdvancedMarker, Pin } from '@vis.gl/react-google-maps'
import { Loader2, MapPin, Search } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { type NearbyPlaceResult, reGeocodePlace, searchPlacesGoogle } from '@/api/services/overland'
import type { Place } from '@/api/types/overland'
import { GoogleCircleMarker } from '@/components/shared/map/google-circle-marker'
import { GoogleMapCanvas } from '@/components/shared/map/google-map-canvas'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  place: Place
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdated: (updatedPlace: Place) => void | Promise<void>
}

export const GooglePlaceSelectDialog = ({ place, open, onOpenChange, onUpdated }: Props) => {
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<NearbyPlaceResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedResult, setSelectedResult] = useState<NearbyPlaceResult | null>(null)
  const { isSubmitting: isApplying, run } = useSubmitLock()

  // ダイアログ表示時に自動検索
  useEffect(() => {
    if (!open) return
    setKeyword('')
    setResults([])
    setSelectedResult(null)
    const search = async () => {
      setIsSearching(true)
      setSelectedResult(null)
      try {
        const searchResults = await searchPlacesGoogle(
          place.latitude,
          place.longitude,
          place.auto_name || undefined,
          0.5,
          10,
        )
        setResults(searchResults)
        if (searchResults.length === 0) {
          toast.info('候補が見つかりませんでした')
        }
      } catch {
        toast.error('Google検索に失敗しました')
      } finally {
        setIsSearching(false)
      }
    }
    void search()
  }, [open, place.latitude, place.longitude, place.auto_name])

  // Google検索
  const handleSearch = useCallback(async () => {
    setIsSearching(true)
    setSelectedResult(null)
    try {
      const searchResults = await searchPlacesGoogle(
        place.latitude,
        place.longitude,
        keyword.trim() || place.auto_name || undefined,
        0.5,
        10,
      )
      setResults(searchResults)
      if (searchResults.length === 0) {
        toast.info('候補が見つかりませんでした')
      }
    } catch {
      toast.error('Google検索に失敗しました')
    } finally {
      setIsSearching(false)
    }
  }, [place.latitude, place.longitude, place.auto_name, keyword])

  // 選択したPlaceで更新
  const handleApply = useCallback(async () => {
    const googlePlaceId = selectedResult?.google_place_id
    if (!selectedResult || !googlePlaceId) {
      toast.error('Google Place IDがありません')
      return
    }

    await run(async () => {
      try {
        const updated = await reGeocodePlace(place.id, googlePlaceId, selectedResult.name)
        toast.success(`「${updated.display_name}」に更新しました`)
        await onUpdated(updated)
        onOpenChange(false)
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }, [place.id, selectedResult, onUpdated, onOpenChange, run])
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-350 max-h-[95vh] h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="size-5" />
            Googleで場所を再取得
          </DialogTitle>
          <DialogDescription>「{place.display_name}」の代わりとなるGoogle Placeを選択してください。</DialogDescription>
        </DialogHeader>

        {/* 検索バー */}
        <div className="flex gap-2">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="キーワードで絞り込み..."
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button onClick={handleSearch} disabled={isSearching} size="icon" variant="outline">
            {isSearching ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </Button>
        </div>

        {/* 地図 + 検索結果の縦配置レイアウト */}
        <div className="flex-1 overflow-hidden flex flex-col gap-4 min-h-0">
          {/* 上側：地図 */}
          <div className="h-2/5 rounded-lg overflow-hidden border">
            <GoogleMapCanvas
              defaultCenter={{ lat: place.latitude, lng: place.longitude }}
              defaultZoom={16}
              className="w-full h-full"
            >
              {/* 既存のPlace（青いピンマーカー） */}
              <AdvancedMarker position={{ lat: place.latitude, lng: place.longitude }}>
                <Pin background="#3B82F6" borderColor="#1D4ED8" glyphColor="#FFFFFF" />
              </AdvancedMarker>

              {/* 検索候補（円形マーカー） */}
              {results.map((result, idx) => {
                const isSelected =
                  selectedResult?.google_place_id === result.google_place_id && selectedResult?.name === result.name
                return (
                  <GoogleCircleMarker
                    key={`${result.google_place_id ?? result.name}-${idx}`}
                    position={{ lat: result.latitude, lng: result.longitude }}
                    radius={isSelected ? 12 : 8}
                    color={isSelected ? '#EF4444' : '#F97316'}
                    fillColor={isSelected ? '#EF4444' : '#F97316'}
                    fillOpacity={isSelected ? 0.8 : 0.6}
                    weight={isSelected ? 3 : 2}
                    zIndex={isSelected ? 1 : 0}
                    onClick={() => setSelectedResult(result)}
                  />
                )
              })}
            </GoogleMapCanvas>
          </div>

          {/* 下側：検索結果リスト */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MapPin className="size-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">検索ボタンを押して候補を取得</p>
                </div>
              ) : (
                <div>
                  {results.map((result, idx) => (
                    <button
                      key={`${result.google_place_id ?? result.name}-${idx}`}
                      type="button"
                      onClick={() => setSelectedResult(result)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors flex gap-3 ${
                        selectedResult?.google_place_id === result.google_place_id &&
                        selectedResult?.name === result.name
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:bg-accent'
                      }`}
                    >
                      {/* 情報 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div className="min-w-0 font-medium text-sm">{result.name}</div>
                          <VisitedPlaceBadge visitCount={result.visit_count} lastVisitedAt={result.last_visited_at} />
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          {result.distance_m !== null && <span>{Math.round(result.distance_m)}m</span>}
                          {result.category && <span>{result.category}</span>}
                        </div>
                        {result.address && (
                          <div className="text-xs text-muted-foreground mt-1 truncate">{result.address}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 適用ボタン */}
        {selectedResult && (
          <Button onClick={handleApply} disabled={isApplying} className="w-full gap-0 p-2!">
            {isApplying ? (
              <>
                <Loader2 className="size-4 mr-2 shrink-0 animate-spin" />
                <span className="truncate">更新中...</span>
              </>
            ) : (
              <>
                <MapPin className="size-4 shrink-0" />
                <span className="truncate">「{selectedResult.name}</span>
                <span> 」で更新</span>
              </>
            )}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  )
}
